import React from "react";
import { formatOrdinal } from "@/helpers/format.helpers";
import { faClock } from "@fortawesome/free-regular-svg-icons";
import { faTrophy } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface WinnerDropBadgeProps {
  rank: number | null;
  position?: number | undefined;
  decisionTime: number | null;
  variant?: "default" | "simple" | undefined;
}

const WinnerDropBadge: React.FC<WinnerDropBadgeProps> = ({
  rank,
  position = 1,
  decisionTime,
  variant = "default",
}) => {
  const effectiveRank = rank ?? position;
  if (effectiveRank === 0 || Number.isNaN(effectiveRank)) return null;

  const rankNumber = effectiveRank;

  const dateTime =
    decisionTime !== null && decisionTime !== 0 && !Number.isNaN(decisionTime)
      ? new Date(decisionTime)
      : null;

  const dateString = dateTime
    ? dateTime.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
      })
    : null;

  const timeString = dateTime
    ? dateTime.toLocaleString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  let colorClasses = "";
  let textColorClass = "";
  const rankText = formatOrdinal(rankNumber);

  switch (rankNumber) {
    case 1:
      colorClasses =
        "tw-bg-yellow-500/10 tw-text-[#ffc107] tw-border-yellow-500/20";
      textColorClass = "tw-text-[#ffc107]";
      break;
    case 2:
      colorClasses = "tw-bg-iron-400/10 tw-text-iron-300 tw-border-iron-400/20";
      textColorClass = "tw-text-iron-300";
      break;
    case 3:
      colorClasses =
        "tw-bg-amber-600/10 tw-text-amber-500 tw-border-amber-600/20";
      textColorClass = "tw-text-amber-600";
      break;
    default:
      colorClasses = "tw-bg-iron-600/20 tw-text-iron-400 tw-border-iron-600/20";
      textColorClass = "tw-text-iron-500";
  }

  if (variant === "simple") {
    return (
      <div
        className={`tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-semibold ${textColorClass}`}
      >
        <FontAwesomeIcon icon={faTrophy} className="tw-h-3.5 tw-w-3.5" />
        <span>
          {rankText}
          {position > 1 && ` #${position}`}
        </span>
        {dateString && (
          <span>
            · {dateString}
            {timeString && (
              <span className="tw-hidden sm:tw-inline">, {timeString}</span>
            )}
          </span>
        )}
      </div>
    );
  }

  return (
    <div
      className={`tw-flex tw-items-center tw-gap-1 tw-whitespace-nowrap tw-rounded tw-border tw-border-solid tw-px-1.5 tw-py-0.5 tw-text-[11px] tw-font-medium ${colorClasses}`}
    >
      <FontAwesomeIcon icon={faTrophy} className="tw-size-2" />
      {rankText}
      {position > 1 && <span>#{position}</span>}

      {dateString && (
        <span className="tw-hidden tw-items-center md:tw-flex">
          <div className="tw-size-[3px] tw-rounded-full tw-bg-current tw-opacity-70"></div>
          <span className="tw-border-current/40 tw-flex tw-items-center tw-border-l tw-px-2">
            <FontAwesomeIcon icon={faClock} className="tw-mr-1.5 tw-size-2.5" />
            {dateString}
            {timeString && (
              <span className="tw-hidden sm:tw-inline">, {timeString}</span>
            )}
          </span>
        </span>
      )}
    </div>
  );
};

export default WinnerDropBadge;
